// Runs candidate code in the browser: Python through Pyodide (the same version the Python
// course uses), JavaScript in a plain worker. Used for the candidate's ▶ Run button and for
// hidden-test grading on the admin page, so no code ever runs on the server.
//
// Isolation: everything runs inside a sandboxed iframe (sandbox="allow-scripts", no
// allow-same-origin), so the code has an opaque "null" origin and cannot read the site's
// storage or make requests with the signed-in user's session. Inside the frame, each run
// happens in a Web Worker with a time limit, so an infinite loop is stopped instead of
// freezing the page.

const PYODIDE_URL = 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/'
const RUN_TIMEOUT_MS = 5000
const PYODIDE_LOAD_TIMEOUT_MS = 90000

const pythonWorkerSource = `
importScripts('${PYODIDE_URL}pyodide.js');
const ready = loadPyodide({ indexURL: '${PYODIDE_URL}' });
ready.then(() => postMessage({ type: 'ready' }), err => postMessage({ type: 'load-error', error: String(err) }));
const RUNNER = \`
import sys, io, traceback
_out = io.StringIO()
sys.stdin = io.StringIO(_stdin)
sys.stdout = _out
sys.stderr = _out
try:
    exec(compile(_code, 'main.py', 'exec'), {'__name__': '__main__'})
except SystemExit:
    pass
except BaseException:
    traceback.print_exc()
finally:
    sys.stdout = sys.__stdout__
    sys.stderr = sys.__stderr__
_out.getvalue()
\`;
onmessage = async (e) => {
  const pyodide = await ready;
  pyodide.globals.set('_code', e.data.code);
  pyodide.globals.set('_stdin', e.data.stdin);
  try {
    const output = await pyodide.runPythonAsync(RUNNER);
    postMessage({ type: 'result', output });
  } catch (err) {
    postMessage({ type: 'result', output: '', error: String(err && err.message || err) });
  }
};
`

// Gives the starter code what it expects from Node: require('fs').readFileSync(0) for stdin
// and console.log for output.
const jsWorkerSource = `
onmessage = (e) => {
  const lines = [];
  const fmt = (v) => typeof v === 'string' ? v : (v === undefined ? 'undefined' : (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)));
  const write = (...args) => lines.push(args.map(fmt).join(' '));
  const fakeConsole = { log: write, info: write, warn: write, error: write };
  const fs = { readFileSync: () => e.data.stdin };
  const require = (name) => {
    if (name === 'fs') return fs;
    throw new Error("Cannot find module '" + name + "' (only 'fs' is available here)");
  };
  const process = { stdout: { write: (s) => { lines.push(String(s).replace(/\\n$/, '')); return true; } }, exit: () => { throw { __exit: true }; } };
  try {
    new Function('require', 'console', 'process', 'module', 'exports', e.data.code)(require, fakeConsole, process, { exports: {} }, {});
  } catch (err) {
    // The stack only shows this runner's internals, so the message alone is clearer.
    if (!(err && err.__exit)) lines.push(String(err));
  }
  postMessage({ type: 'result', output: lines.join('\\n') });
};
`

// Runs inside the sandboxed frame. Replies to the parent with { type: 'result', id, output,
// error, timedOut, loadFailed }; loadFailed means the runner itself could not work (e.g.
// Python failed to download), as opposed to the candidate's code failing.
const frameScript = `
const PY_SRC = ${JSON.stringify(pythonWorkerSource)};
const JS_SRC = ${JSON.stringify(jsWorkerSource)};
const RUN_TIMEOUT_MS = ${RUN_TIMEOUT_MS};
const PYODIDE_LOAD_TIMEOUT_MS = ${PYODIDE_LOAD_TIMEOUT_MS};
const stoppedMessage = 'Stopped after ' + (RUN_TIMEOUT_MS / 1000) + ' seconds - is there an infinite loop?';
const workerFrom = (src) => new Worker(URL.createObjectURL(new Blob([src], { type: 'text/javascript' })));

const python = {
  worker: null,
  ready: null,
  warmUp() {
    if (this.ready) return this.ready;
    const worker = this.worker = workerFrom(PY_SRC);
    this.ready = new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Python took too long to load. Check the internet connection and try again.')), PYODIDE_LOAD_TIMEOUT_MS);
      worker.onmessage = (e) => {
        clearTimeout(timer);
        if (e.data.type === 'ready') resolve();
        else reject(new Error('Python could not be loaded. Check the internet connection and try again.'));
      };
      worker.onerror = () => { clearTimeout(timer); reject(new Error('Python could not be loaded.')); };
    }).catch((err) => { this.reset(); throw err; });
    return this.ready;
  },
  reset() {
    if (this.worker) this.worker.terminate();
    this.worker = null;
    this.ready = null;
  },
  async run(code, stdin) {
    await this.warmUp();
    const worker = this.worker;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        // The only way to stop runaway code; the next run reloads Python.
        this.reset();
        resolve({ output: '', error: stoppedMessage, timedOut: true });
      }, RUN_TIMEOUT_MS);
      worker.onmessage = (e) => {
        if (e.data.type !== 'result') return;
        clearTimeout(timer);
        resolve({ output: e.data.output || '', error: e.data.error || null });
      };
      worker.postMessage({ code, stdin });
    });
  }
};

function runJavaScript(code, stdin) {
  const worker = workerFrom(JS_SRC);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      worker.terminate();
      resolve({ output: '', error: stoppedMessage, timedOut: true });
    }, RUN_TIMEOUT_MS);
    worker.onmessage = (e) => {
      clearTimeout(timer);
      worker.terminate();
      resolve({ output: e.data.output || '', error: e.data.error || null });
    };
    worker.onerror = (e) => {
      clearTimeout(timer);
      worker.terminate();
      resolve({ output: '', error: e.message || 'The code could not run' });
    };
    worker.postMessage({ code, stdin });
  });
}

window.addEventListener('message', async (e) => {
  const msg = e.data || {};
  if (msg.type === 'warmup') {
    if (msg.language === 'python') python.warmUp().catch(() => {});
    return;
  }
  if (msg.type !== 'run') return;
  let result;
  try {
    result = msg.language === 'python' ? await python.run(msg.code, msg.stdin) : await runJavaScript(msg.code, msg.stdin);
  } catch (err) {
    result = { output: '', error: String((err && err.message) || err), loadFailed: true };
  }
  e.source.postMessage(Object.assign({ type: 'result', id: msg.id }, result), '*');
});
parent.postMessage({ type: 'frame-ready' }, '*');
`

class IsolatedRunner {
  constructor() {
    this.frame = null
    this.readyPromise = null
    this.pending = new Map()
    this.nextId = 1
    this.onMessage = this.onMessage.bind(this)
  }

  start() {
    if (this.readyPromise) return this.readyPromise
    const frame = document.createElement('iframe')
    frame.setAttribute('sandbox', 'allow-scripts')
    frame.setAttribute('aria-hidden', 'true')
    frame.title = 'Code runner'
    frame.style.display = 'none'
    frame.srcdoc = `<!doctype html><meta charset="utf-8"><script>${frameScript}<\/script>`
    this.frame = frame
    this.readyPromise = new Promise((resolve, reject) => {
      this.resolveReady = resolve
      setTimeout(() => reject(new Error('The code runner did not start.')), 15000)
    }).catch(err => {
      this.destroy()
      throw err
    })
    window.addEventListener('message', this.onMessage)
    document.body.appendChild(frame)
    return this.readyPromise
  }

  onMessage(e) {
    if (!this.frame || e.source !== this.frame.contentWindow) return
    const msg = e.data || {}
    if (msg.type === 'frame-ready') this.resolveReady?.()
    if (msg.type !== 'result') return
    const pending = this.pending.get(msg.id)
    if (!pending) return
    this.pending.delete(msg.id)
    clearTimeout(pending.timer)
    pending.resolve({ output: msg.output || '', error: msg.error || null, timedOut: !!msg.timedOut, loadFailed: !!msg.loadFailed })
  }

  async run(language, code, stdin) {
    try {
      await this.start()
    } catch (err) {
      return { output: '', error: err.message, loadFailed: true }
    }
    const id = this.nextId++
    return new Promise((resolve) => {
      // Safety net in case the frame never answers (it has its own, shorter limits).
      const timer = setTimeout(() => {
        this.pending.delete(id)
        resolve({ output: '', error: 'The code runner stopped responding.', loadFailed: true })
      }, PYODIDE_LOAD_TIMEOUT_MS + RUN_TIMEOUT_MS + 10000)
      this.pending.set(id, { resolve, timer })
      this.frame.contentWindow.postMessage({ type: 'run', id, language, code, stdin }, '*')
    })
  }

  warmUp(language) {
    this.start()
      .then(() => this.frame.contentWindow.postMessage({ type: 'warmup', language }, '*'))
      .catch(() => {})
  }

  destroy() {
    window.removeEventListener('message', this.onMessage)
    this.frame?.remove()
    this.frame = null
    this.readyPromise = null
  }
}

const runner = new IsolatedRunner()

export function warmUpRunner(language) {
  runner.warmUp(language)
}

// Resolves to { output, error, timedOut, loadFailed }. Never rejects.
export function runInBrowser(language, code, stdin) {
  return runner.run(language, code, stdin)
}
